/**
 * Operator view + lead-generation next-action mapping for the glow-up.
 */
import { escapeAttr, escapeHtml } from "./dashboard-html.js";
import type { LeadFactoryLead } from "./dashboard-lead-factory-types.js";
import { pipelineOperatorNavHtml, readPipelineOperatorView } from "./dashboard-pipeline-nav.js";

export const dashboardDefaultView = "workflow";

export const leadGenerationOverflowActions = [
  { action: "assign", label: "Assign" },
  { action: "enrich", label: "Enrich" },
  { action: "ready", label: "Ready" },
  { action: "campaign", label: "Campaign" },
  { action: "suppress", label: "Suppress", danger: true }
] as const;

export const outOfStageLeadActions = [
  { action: "pipeline", label: "Pipeline" },
  { action: "offer", label: "Offer" },
  { action: "offer-document", label: "Offer Doc" },
  { action: "offer-sent", label: "Mark Sent" },
  { action: "contract-checklist", label: "Contract" },
  { action: "contract-packet", label: "Packet" },
  { action: "title-handoff", label: "Title" },
  { action: "title-clear", label: "Title Clear" },
  { action: "title-issue", label: "Title Issue" },
  { action: "closing-schedule", label: "Closing" },
  { action: "closing-complete", label: "Closed" },
  { action: "closing-artifact", label: "Closing Artifact" },
  { action: "portfolio-handoff", label: "Portfolio" },
  { action: "asset-onboarding", label: "Onboard Asset" },
  { action: "disposition-review", label: "Disposition" },
  { action: "disposition-disposed", label: "Disposed" },
  { action: "finance-record", label: "Finance" },
  { action: "merge", label: "Merge" },
  { action: "unmerge", label: "Unmerge" },
  { action: "split", label: "Split" }
] as const;

export function readDashboardView(): string {
  if (typeof window === "undefined") return dashboardDefaultView;
  return readPipelineOperatorView();
}

export function mappedLeadGenerationNextAction(lead: Pick<LeadFactoryLead, "contact_readiness">): { action: string; label: string } | null {
  const readiness = lead.contact_readiness || "";
  if (readiness === "needs_enrichment" || readiness.startsWith("not_ready")) {
    return { action: "enrich", label: "Enrich" };
  }
  return null;
}

export function leadRowPrimaryActionsHtml(leadId: string, next: { action: string; label: string } | null): string {
  if (!next) return leadActionButtonHtml(leadId, "detail", "Detail");
  return `${leadActionButtonHtml(leadId, "detail", "Detail")}${leadActionButtonHtml(leadId, next.action, next.label, { primary: true })}`;
}

export function leadActionButtonHtml(
  leadId: string,
  action: string,
  label: string,
  options: { danger?: boolean; primary?: boolean } = {}
): string {
  const kind = options.danger ? "ghost danger" : options.primary ? "primary" : "secondary";
  return `<button class="button ${kind}" type="button" data-lead-action="${escapeAttr(action)}" data-lead-id="${escapeAttr(leadId)}">${escapeHtml(label)}</button>`;
}

export function leadGenerationOverflowHtml(leadId: string, hideAction?: string): string {
  const actions = leadGenerationOverflowActions.filter((item) => item.action !== hideAction);
  if (actions.length === 0) return "";
  return `
    <details class="lead-row-overflow">
      <summary class="button secondary">More</summary>
      <div class="row-actions">
        ${actions.map((item) => leadActionButtonHtml(leadId, item.action, item.label, { danger: "danger" in item && item.danger })).join("")}
      </div>
    </details>
  `;
}

export function dashboardNavHtml(view: string): string {
  const pipelineView = view === "ingest" || view === "raise" || view === "workflow" ? view : "workflow";
  return pipelineOperatorNavHtml(pipelineView);
}

export function outOfStageLeadActionsHtml(leadId: string): string {
  return `
    <div class="row-actions" data-lead-detail-out-of-stage-actions>
      ${outOfStageLeadActions.map((item) => leadActionButtonHtml(leadId, item.action, item.label)).join("")}
    </div>
  `;
}
